import {memo} from 'react';
import {useSelector} from 'react-redux';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {generateHeadcountString} from '~/ViewInvitation/components/Guestlist/helpers/utils';
import {selectOptions} from '~/ViewInvitation/selectors/eviteEvent';
import {DeliveryStatus, type Guest, isHostGuest} from '~/ViewInvitation/types/guests';
import {deliveryStatusToString} from '~/ViewInvitation/utils/guests';
import {Typography} from '~library/atoms/Typography';

interface StatusTextProps {
  guest: Guest;
}

export const StatusText = memo(({guest}: StatusTextProps) => {
  const isHost = useSelector(selectIsHost);
  const familyHeadcountEnabled = useSelector(selectOptions)?.event_option_allow_kids;

  if (isHost && isHostGuest(guest)) {
    if (guest.deliveryStatus === DeliveryStatus.FAILED) {
      return (
        <Typography
          variant="paragraph3"
          color="feedbackError"
          data-qa-id="guest-status"
          data-qa-value="Not delivered"
          data-qa-status={DeliveryStatus.FAILED}
        >
          Not delivered
        </Typography>
      );
    }
    if (['maybe', 'yes'].includes(guest.rsvpResponse)) {
      const headcount = generateHeadcountString(guest, familyHeadcountEnabled);
      return (
        <Typography
          variant="paragraph3"
          color="asphalt"
          data-qa-id="guest-status"
          data-qa-value={headcount}
          data-qa-status={guest.rsvpResponse}
        >
          {headcount}
        </Typography>
      );
    }
    if (guest.rsvpResponse === 'no') {
      return (
        <Typography
          variant="paragraph3"
          color="asphalt"
          data-qa-id="guest-status"
          data-qa-value="Not attending"
          data-qa-status={guest.rsvpResponse}
        >
          Not attending
        </Typography>
      );
    }
    const status = deliveryStatusToString(guest);
    return (
      <Typography
        variant="paragraph3"
        color="asphalt"
        data-qa-id="guest-status"
        data-qa-value={status}
        data-qa-status={guest.rsvpResponse}
      >
        {status}
      </Typography>
    );
  }
  if (guest.rsvpResponse === 'undecided') {
    return (
      <Typography
        variant="paragraph3"
        color="asphalt"
        data-qa-id="guest-status"
        data-qa-value="No reply"
        data-qa-status={guest.rsvpResponse}
      >
        No reply
      </Typography>
    );
  }
  if (guest.rsvpResponse === 'no') {
    return (
      <Typography
        variant="paragraph3"
        color="asphalt"
        data-qa-id="guest-status"
        data-qa-value="Not attending"
        data-qa-status={guest.rsvpResponse}
      >
        Not attending
      </Typography>
    );
  }
  const headcount = generateHeadcountString(guest, familyHeadcountEnabled);
  return (
    <Typography
      variant="paragraph3"
      color="asphalt"
      data-qa-id="guest-status"
      data-qa-value={headcount}
      data-qa-status={guest.rsvpResponse}
    >
      {headcount}
    </Typography>
  );
});

StatusText.displayName = 'StatusText';
